import { useEffect, useState } from "react";

import { api } from "@/lib/api";

// ---------------------------------------------------------------------------
// Global presence — who's online right now.
//
// Two data sources fused:
//   1. GET /api/v1/presence/  → initial snapshot on first mount
//   2. The notification WebSocket pushes `presence.change` events as
//      users connect/disconnect, which delta-update the set.
//
// Module-level state so multiple consumers share a single set + a
// single subscription (no point opening the WS twice for the same
// reason). Hook just subscribes to changes.
// ---------------------------------------------------------------------------
let online = new Set<string>();
let listeners = new Set<() => void>();
let primed = false;

function notify() { for (const cb of listeners) cb(); }

function applyDelta(userId: string, isOnline: boolean) {
  if (isOnline) online.add(userId);
  else          online.delete(userId);
  notify();
}

/** Called from the NotificationConsumer's onmessage handler — see
 *  `useNotificationSocket` for the wire-up. */
export function pushPresenceDelta(userId: string, isOnline: boolean) {
  applyDelta(userId, isOnline);
}

async function primeFromAPI() {
  if (primed) return;
  primed = true;
  try {
    const { data } = await api.get<{ online_user_ids: string[] }>("/presence/");
    online = new Set(data.online_user_ids);
    notify();
  } catch {
    // The WS feed will populate it eventually; primed=true so we don't
    // hammer the endpoint on retry.
    primed = false;
  }
}

/** Periodic re-prime so ghost users (browser killed without firing the
 *  WS disconnect handler) get pruned promptly. Hitting GET /presence/
 *  triggers the server-side snapshot which sweeps stale heartbeat
 *  entries and broadcasts presence.change for any user it removes.
 *  Single timer for the whole app — module-level so re-mounting a
 *  consumer doesn't multiply it. */
let refreshTimer: ReturnType<typeof setInterval> | null = null;
const REFRESH_INTERVAL_MS = 45_000;

async function refreshFromAPI() {
  try {
    const { data } = await api.get<{ online_user_ids: string[] }>("/presence/");
    const next = new Set(data.online_user_ids);
    // Only notify if the set actually changed; spares an unnecessary
    // re-render storm across every avatar on the page.
    if (next.size !== online.size || ![...next].every((u) => online.has(u))) {
      online = next;
      notify();
    }
  } catch {
    /* network blip — try again next tick */
  }
}

function ensureRefreshTimer() {
  if (refreshTimer != null) return;
  refreshTimer = setInterval(refreshFromAPI, REFRESH_INTERVAL_MS);
}

/** Returns `{isOnline(userId)}` — re-renders only when the set changes. */
export function usePresence() {
  // Cheap version counter; bumping it forces consumers to re-read.
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const cb = () => setVersion((v) => v + 1);
    listeners.add(cb);
    primeFromAPI();
    ensureRefreshTimer();
    return () => { listeners.delete(cb); };
  }, []);

  return {
    isOnline: (userId: string | null | undefined) =>
      !!userId && online.has(String(userId)),
    // Exposed mostly for diagnostics — usually just call isOnline().
    onlineCount: online.size,
    version,
  };
}
